import { resolvedConcentrationDisplay } from './concentrationResolver.js'

// Plain concentration labels to strip from fragrance names for grouping.
// Longer patterns first so "eau de parfum" wins over the "parfum" it ends with.
// Flanker markers (Intense, Elixir, Extrait, Absolu, Eau Fraîche…) are NOT in
// this list on purpose: they name a different fragrance, so they stay in the
// grouping key.
const PLAIN_CONCENTRATIONS = [
  'eau de parfum',
  'eau de toilette',
  'eau de cologne',
  'le parfum',
  'edp',
  'edt',
  'edc',
  'parfum',
]

// A query term and a row label describe the same concentration when they map
// to the same canonical form: "EDP" and "Eau de Parfum" are one filter, and
// "Le Parfum" is a parfum. This is presentation only; it never rewrites data.
const CANONICAL_CONCENTRATION = Object.freeze({
  'eau de parfum': 'edp',
  edp: 'edp',
  'eau de toilette': 'edt',
  edt: 'edt',
  'eau de cologne': 'edc',
  edc: 'edc',
  'le parfum': 'parfum',
  parfum: 'parfum',
})

const clean = value => String(value || '').trim().replace(/\s+/g, ' ')
const normalized = value => clean(value).toLowerCase()
const escapeRegex = value => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// Whole-word match only. "Parfums de Marly" is a house, not a Parfum filter,
// and "edpense" contains no EDP. A term counts when it is bounded by the start
// or end of the query or by a non-alphanumeric character on both sides.
function containsConcentrationTerm(value, concentration) {
  const lower = normalized(value)
  const pattern = new RegExp(`(^|[^a-z0-9])${escapeRegex(concentration)}(?=$|[^a-z0-9])`)
  return pattern.test(lower)
}

function stripLeadingHouse(name, house) {
  const cleanName = clean(name)
  const cleanHouse = clean(house)
  if (!cleanHouse || normalized(cleanName) === normalized(cleanHouse)) return cleanName
  if (!normalized(cleanName).startsWith(`${normalized(cleanHouse)} `)) return cleanName
  return clean(cleanName.slice(cleanHouse.length))
}

// The longest plain concentration a label ends with, or null. "Sauvage Eau de
// Parfum" yields "eau de parfum", never the shorter "parfum" it also ends with.
function trailingConcentration(value) {
  const lower = normalized(value)
  return PLAIN_CONCENTRATIONS.find(conc => lower === conc || lower.endsWith(` ${conc}`)) || null
}

function stripPlainConcentrations(name) {
  const lower = normalized(name)

  for (const conc of PLAIN_CONCENTRATIONS) {
    // Only strip if it's the entire name (don't strip "Parfum" from "Fame Parfum" the fragrance name)
    if (lower === conc) return name

    // Strip trailing concentration labels (e.g., "Sauvage Eau de Parfum" → "Sauvage")
    if (lower.endsWith(` ${conc}`)) {
      // Calculate how many characters to remove (+ 1 for the space)
      const removeLength = conc.length + 1
      const candidate = clean(name.slice(0, name.length - removeLength))
      if (candidate.length >= 1) return candidate
    }
  }

  return name
}

/**
 * Derive the grouping key for a fragrance — house + name with only plain
 * concentrations stripped. Flankers (Intense, Elixir, etc.) remain part of the name.
 *
 * Examples:
 * - "Sauvage Eau de Parfum" → "Sauvage"
 * - "Sauvage Elixir" → "Sauvage Elixir" (flanker stays)
 * - "Fame Parfum" → "Fame" (plain concentration suffix stripped)
 * - "Dior Sauvage EDP" → "Sauvage" (house prefix + concentration both stripped)
 */
export function fragranceGroupingKey(row) {
  if (!row?.name || !row?.house) return null
  const withoutHouse = stripLeadingHouse(row.name, row.house)
  const withoutConc = stripPlainConcentrations(withoutHouse)
  // NUL separates house from name because it cannot occur in either.
  return `${normalized(row.house)}\u0000${normalized(withoutConc)}`
}

/**
 * Display name for a group — the shortest representative name without concentration suffix
 */
export function fragranceGroupingName(row) {
  if (!row?.name || !row?.house) return row?.name || ''
  const withoutHouse = stripLeadingHouse(row.name, row.house)
  return stripPlainConcentrations(withoutHouse)
}

/**
 * Group search results by fragrance name (house + name after stripping concentrations).
 * Returns array of groups, each with:
 *   - name: display name (shortest form)
 *   - house: house name
 *   - variants: array of rows with this name
 *   - first_rank: position of the first variant in the original results
 *
 * @param {Array} rows - Search result rows (catalog or collection items)
 * @param {Function} [rankFn] - Optional function to extract rank from row (default: index)
 */
export function groupFragrancesByName(rows = [], rankFn = (_, index) => index) {
  if (!rows || rows.length === 0) return []

  const groups = new Map()

  rows.forEach((row, index) => {
    const key = fragranceGroupingKey(row)
    if (!key) return

    const rank = rankFn(row, index)

    if (!groups.has(key)) {
      groups.set(key, {
        name: fragranceGroupingName(row),
        house: clean(row.house),
        variants: [],
        first_rank: rank,
      })
    }

    const group = groups.get(key)
    group.variants.push(row)
    group.first_rank = Math.min(group.first_rank, rank)
  })

  return Array.from(groups.values())
    .sort((a, b) => a.first_rank - b.first_rank)
}

/**
 * Canonical concentrations named as whole words in a search query.
 * "sauvage edp" → ['edp']; "parfums de marly" → [] (no whole-word match).
 */
export function queryConcentrations(query) {
  let remaining = normalized(query)
  const found = []
  // Longest terms first, and each match is blanked out before the shorter
  // terms are tried: "eau de parfum" is one EDP filter, not also a Parfum one.
  for (const conc of PLAIN_CONCENTRATIONS) {
    if (!containsConcentrationTerm(remaining, conc)) continue
    found.push(CANONICAL_CONCENTRATION[conc])
    remaining = remaining.replace(
      new RegExp(`(^|[^a-z0-9])${escapeRegex(conc)}(?=$|[^a-z0-9])`, 'g'),
      '$1#',
    )
  }
  return [...new Set(found)]
}

/**
 * Check if a search query contains a concentration term that should filter sub-rows
 */
export function queryHasConcentration(query) {
  return queryConcentrations(query).length > 0
}

/**
 * Check if a variant row matches a concentration filter in the query.
 * The row's concentration comes from the resolver display first, then the raw
 * `concentration` value, then a trailing concentration in its name. Each is
 * compared canonically, so an "Eau de Parfum" row never satisfies a "Parfum"
 * filter merely because its label ends with that word.
 */
export function variantMatchesConcentrationFilter(row, query) {
  const wanted = queryConcentrations(query)
  if (!wanted.length) return false

  const resolved = normalized(resolvedConcentrationDisplay(row))
  const rowConc = resolved === 'unknown' || resolved === 'ambiguous'
    ? normalized(row?.concentration)
    : resolved
  const candidates = [
    CANONICAL_CONCENTRATION[rowConc],
    CANONICAL_CONCENTRATION[trailingConcentration(rowConc)],
    CANONICAL_CONCENTRATION[trailingConcentration(row?.name)],
  ].filter(Boolean)

  return candidates.some(candidate => wanted.includes(candidate))
}
